import { memo } from 'react';
import { useMantineColorScheme, useMantineTheme } from '@mantine/core';
import styles from './OnboardingMapIllustration.module.css';

const ROAD_OPACITY_DARK = { wide: 0.04, edge: 0.12, dash: 0.2 };
const ROAD_OPACITY_LIGHT = { wide: 0.06, edge: 0.12, dash: 0.5 };
const PIN_OPACITY_DARK = { outer: 0.05, inner: 0.12 };
const PIN_OPACITY_LIGHT = { outer: 0.06, inner: 0.15 };

export const OnboardingMapIllustration = memo(
  function OnboardingMapIllustration() {
    const { colorScheme } = useMantineColorScheme();
    const theme = useMantineTheme();
    const isDark = colorScheme === 'dark';

    const bgColor = isDark ? theme.colors.dark[8] : theme.colors.gray[0];
    const gridColor = isDark ? theme.colors.dark[5] : theme.colors.gray[2];
    const gridWidth = isDark ? '0.5' : '0.7';
    const accent = isDark ? theme.colors.orange[6] : theme.colors.indigo[6];
    const dashColor = isDark ? 'rgba(255,255,255,0.15)' : 'white';

    const roadOp = isDark ? ROAD_OPACITY_DARK : ROAD_OPACITY_LIGHT;
    const pinOp = isDark ? PIN_OPACITY_DARK : PIN_OPACITY_LIGHT;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    return (
      <svg
        viewBox="0 0 500 900"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
        className={styles.illustration}
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="oGrid"
            width="50"
            height="50"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 50 0 L 0 0 0 50"
              fill="none"
              stroke={gridColor}
              strokeWidth={gridWidth}
            />
          </pattern>
          {!isDark && (
            <radialGradient id="oWarmGlow" cx="30%" cy="40%" r="60%">
              <stop offset="0%" stopColor={theme.colors.indigo[0]} />
              <stop offset="100%" stopColor={theme.white} />
            </radialGradient>
          )}
        </defs>

        <rect
          width="500"
          height="900"
          fill={isDark ? bgColor : 'url(#oWarmGlow)'}
        />
        <rect width="500" height="900" fill="url(#oGrid)" />

        {/* Road 1 — main horizontal */}
        <path
          d="M-20 200 Q120 150 250 180 T520 120"
          stroke={accent}
          strokeWidth="32"
          opacity={roadOp.wide}
          strokeLinecap="round"
        />
        <path
          d="M-20 200 Q120 150 250 180 T520 120"
          stroke={accent}
          strokeWidth="2"
          opacity={roadOp.edge}
          strokeLinecap="round"
        />
        <path
          d="M-20 200 Q120 150 250 180 T520 120"
          stroke={dashColor}
          strokeWidth="1.5"
          strokeDasharray="8 14"
          opacity={roadOp.dash}
          strokeLinecap="round"
        />

        {/* Road 2 */}
        <path
          d="M-20 500 Q100 420 240 460 T520 380"
          stroke={accent}
          strokeWidth="24"
          opacity={roadOp.wide * 0.8}
          strokeLinecap="round"
        />
        <path
          d="M-20 500 Q100 420 240 460 T520 380"
          stroke={accent}
          strokeWidth="1.5"
          opacity={roadOp.edge * 0.8}
          strokeLinecap="round"
        />

        {/* Road 3 */}
        <path
          d="M-20 750 Q150 680 300 720 T520 660"
          stroke={accent}
          strokeWidth="20"
          opacity={roadOp.wide * 0.65}
          strokeLinecap="round"
        />
        <path
          d="M-20 750 Q150 680 300 720 T520 660"
          stroke={accent}
          strokeWidth="1.5"
          opacity={roadOp.edge * 0.65}
          strokeLinecap="round"
        />

        {/* Vertical road */}
        <path
          d="M200 -20 Q220 200 210 400 T230 920"
          stroke={accent}
          strokeWidth="1.5"
          opacity={roadOp.edge * 0.65}
          strokeLinecap="round"
        />

        {/* Pin 1 — animated */}
        <g transform="translate(180, 170)">
          <circle r="20" fill={accent} opacity={pinOp.outer}>
            {!prefersReducedMotion && (
              <animate
                attributeName="r"
                values="20;28;20"
                dur="3s"
                repeatCount="indefinite"
              />
            )}
          </circle>
          <circle r="8" fill={accent} opacity={pinOp.inner} />
          <circle r="4" fill={accent} />
          <circle r="1.5" fill={bgColor} />
        </g>

        {/* Pin 2 */}
        <g transform="translate(350, 130)">
          <circle r="14" fill={accent} opacity={pinOp.outer * 0.8} />
          <circle r="5" fill={accent} opacity={pinOp.inner * 0.8} />
          <circle r="2.5" fill={accent} />
        </g>

        {/* Pin 3 */}
        <g transform="translate(120, 450)">
          <circle r="16" fill={accent} opacity={pinOp.outer * 0.8} />
          <circle r="6" fill={accent} opacity={pinOp.inner} />
          <circle r="3" fill={accent} />
          <circle r="1" fill={bgColor} />
        </g>

        {/* Pin 4 */}
        <g transform="translate(340, 400)">
          <circle r="12" fill={accent} opacity={pinOp.outer * 0.6} />
          <circle r="4" fill={accent} opacity={pinOp.inner * 0.7} />
          <circle r="2" fill={accent} />
        </g>

        {/* Pin 5 — animated */}
        <g transform="translate(250, 700)">
          <circle r="18" fill={accent} opacity={pinOp.outer}>
            {!prefersReducedMotion && (
              <animate
                attributeName="r"
                values="18;24;18"
                dur="4s"
                repeatCount="indefinite"
              />
            )}
          </circle>
          <circle r="6" fill={accent} opacity={pinOp.inner} />
          <circle r="3" fill={accent} />
          <circle r="1" fill={bgColor} />
        </g>
      </svg>
    );
  }
);
